import { type Ticket, useTicketsStore } from "@/_store/tickets/tickets.module";
import {
  defaultFiltersState,
  type FilterContext,
  type SocTicketsFilters,
} from "@/_store/filters.module";
import { useI18n } from "vue-i18n";
import { useSelectorStore } from "@/_store/selector.module";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import { debounce } from "lodash";
import { TicketsSearchSuggestion, TicketStatus, allUnifiedDlpTriggers } from "@/constants/tickets";
import type { Pagination } from "@/types";
import { type RouteLocationNormalizedLoadedGeneric, useRoute, useRouter } from "vue-router";
import { handleVirtualScrollData } from "@coronet/cui";
import { getDateRangePresets, getNinetyDaysAgo } from "@/constants/date-range-picker.ts";
import { RouteName } from "@/constants/routes.ts";
import { type CuiButtonToggleItem } from "@coronet/cui";
import { useFilters } from "@/composables/useFilters.ts";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
/**
 * We need to rethink this composable.
 * @deprecated
 */
export function useTicketsPage<T extends FilterContext.TICKETS | FilterContext.SOC_TICKETS>(
  filterContext: T,
  isSoc: boolean
) {
  const i18n = useI18n();

  const ticketsStore = useTicketsStore();
  const selectorStore = useSelectorStore();
  const router = useRouter();
  const ticketPreview = ref<Ticket | null>(null);
  const workspaceSearchPage = ref(0);
  const hasMoreWorkspaces = ref(true);
  const workspaces = ref<{ workspaceId: string; workspaceName: string }[]>([]);
  const workspacesLoading = ref(false);

  const { selection } = storeToRefs(selectorStore);
  const { deselectAllPages } = selectorStore;

  const { tickets, pagination, allTickets, loading, showSkeletonLoader, ticketDetailsLoading } =
    storeToRefs(ticketsStore);

  const { resetState, getTickets, resetPagination } = ticketsStore;

  const { localFilters, clearFilters: clearFiltersFromComposable } = useFilters({
    context: filterContext,
    searchKey: "search",
    onFilterChange: async () => {
      resetPagination();
      await getTickets(false);
    },
  });

  const suggestions = computed(() => {
    const baseSuggestions = [
      // the soc search box is already an exact ticket id lookup, so the prefix is redundant there
      ...(isSoc ? [] : [TicketsSearchSuggestion.TICKET_ID]),
      TicketsSearchSuggestion.VIOLATOR,
      TicketsSearchSuggestion.DOMAIN,
      TicketsSearchSuggestion.AFFECTED_USER,
      TicketsSearchSuggestion.PROCESS_NAME,
      TicketsSearchSuggestion.PROCESS_HASH,
      TicketsSearchSuggestion.PATH,
      TicketsSearchSuggestion.COMMAND_LINE,
    ];

    if (localFilters.value.status === TicketStatus.CLOSED) {
      return [...baseSuggestions, TicketsSearchSuggestion.AUTO_RESOLVED_FROM_TIME];
    }
    return baseSuggestions;
  });

  const showClearFiltersButton = computed(() => {
    return !isEqual(
      { ...localFilters.value, status: null },
      { ...defaultFiltersState[filterContext], status: null }
    );
  });

  const clearFilters = () => {
    localFilters.value = cloneDeep({
      ...defaultFiltersState[filterContext],
      status: localFilters.value.status,
    });
  };

  const presets = computed(() => getDateRangePresets());

  // Ticket data lives in a 90-day window (PLAT-4257). Only the ticket logs are
  // bounded — the CSV export modals share this filter bar but keep their own range.
  const minDate = computed(() => getNinetyDaysAgo());

  watch(
    localFilters,
    debounce(() => {
      deselectAllPages();
    }, 300),
    { deep: true }
  );

  const tabs: CuiButtonToggleItem<string>[] = isSoc
    ? [
        {
          value: TicketStatus.OPEN,
          label: i18n.t("general.open"),
        },
        {
          value: TicketStatus.CLOSED,
          label: i18n.t("general.processed"),
        },
      ]
    : [
        {
          value: TicketStatus.OPEN,
          label: i18n.t("general.open"),
        },
        {
          value: TicketStatus.CLOSED,
          label: i18n.t("general.processed"),
        },
        {
          value: TicketStatus.ALL,
          label: i18n.t("general.all"),
        },
      ];

  const currentTab = computed(() => {
    return tabs.find((tab) => tab.value === localFilters.value.status)!;
  });

  const showSubTriggerFilters = computed(() => {
    const triggersInFilter = localFilters.value.eventTriggers || [];
    return allUnifiedDlpTriggers.some((trigger) => triggersInFilter.includes(trigger));
  });

  const showLoader = computed(() => {
    return showSkeletonLoader.value && ticketDetailsLoading.value;
  });

  const onPageChanged = async (payload: Pagination) => {
    if (!isEqual(payload, pagination)) {
      ticketsStore.$patch({ pagination: payload });
      await getTickets(false);
    }
  };

  const applyQueryParams = (route: RouteLocationNormalizedLoadedGeneric) => {
    const result = cloneDeep(localFilters.value);

    Object.keys(localFilters.value).forEach((k) => {
      const param = route.query[k] as string;
      if (param) {
        const key = k as keyof typeof localFilters.value;
        if (Array.isArray(localFilters.value[key])) {
          Object.assign(result, { [key]: param.split(",") });
        } else {
          Object.assign(result, { [key]: param });
        }
      }
    });

    const startTime = route.query.startTime as string;
    const endTime = route.query.endTime as string;

    if (startTime && endTime) {
      result.eventTimeRange = {
        start: startTime,
        end: endTime,
      };
    }

    const shouldMakeRequest = isEqual(localFilters.value, result);
    localFilters.value = cloneDeep(result);

    return shouldMakeRequest;
  };

  const onWorkspaceSearchUpdate = debounce(function (searchString: string) {
    if (searchString !== (localFilters.value as SocTicketsFilters).workspaceId) {
      searchWorkspaces(searchString);
    }
  }, 500);

  async function searchWorkspaces(search = "") {
    workspaceSearchPage.value = 0;
    await fetchWorkspaces(search);
  }

  const { onIntersect } = useInfiniteScroll(workspaces, { hasMore: hasMoreWorkspaces }, () => {
    workspaceSearchPage.value += 1;
    return fetchWorkspaces((localFilters.value as SocTicketsFilters).workspaceId ?? "", true);
  });

  async function fetchWorkspaces(search: string, virtualScroll = false) {
    workspacesLoading.value = true;
    const loadedWorkspaces = await ticketsStore.searchSocWorkspaces({
      search,
      page: workspaceSearchPage.value,
      pageSize: 25,
    });
    hasMoreWorkspaces.value = loadedWorkspaces.length > 25;
    workspaces.value = virtualScroll
      ? handleVirtualScrollData(workspaces.value, loadedWorkspaces, "workspaceId")
      : [...loadedWorkspaces];
    workspacesLoading.value = false;
  }

  const route = useRoute();

  const clearQueryParams = async () => {
    // router replace doesn't work just raw in some cases. This manipulations are done in order to always clear the query
    await nextTick();
    await Promise.resolve(); // This ensures all previous awaits are fully resolved
    router.replace({ query: {} }).catch(() => {});
  };

  onMounted(async () => {
    resetState();
    deselectAllPages();

    ticketsStore.$patch({ isSoc });

    const shouldMakeRequest = applyQueryParams(route);
    if (shouldMakeRequest) {
      await getTickets();
    }
    if (isSoc) {
      await searchWorkspaces();
    }
    await clearQueryParams();
  });

  /**
   * The tickets route has no path params, so pushing it with new query params
   * (the "View" button in the ticket action snackbar) reuses this component instead
   * of remounting it — `onMounted` alone would never apply nor clear them.
   *
   * No route check is needed: navigating away stops this component's effect scope
   * before the watcher can run, so it only ever fires while we are the active page.
   * The empty-query check is what keeps `clearQueryParams` from re-triggering us.
   */
  watch(
    () => route.query,
    async (query) => {
      if (!Object.keys(query).length) return;

      if (applyQueryParams(route)) {
        await getTickets(false);
      }
      await clearQueryParams();
    }
  );

  onBeforeUnmount(() => {
    ticketsStore.$reset();
    clearFiltersFromComposable();
    deselectAllPages();
  });

  return {
    breadCrumbsItems: [
      {
        title: `‹ ${i18n.t("general.home")}`,
        disabled: false,
        to: { name: RouteName.DASHBOARD_PAGE },
      },
    ],
    localFilters,
    showClearFiltersButton,
    clearFilters,
    getTickets,
    showLoader,
    tickets,
    allTickets,
    selection,
    pagination,
    loading,
    onPageChanged,
    tabs,
    currentTab,
    ticketPreview,
    workspaces,
    workspacesLoading,
    onWorkspaceSearchUpdate,
    onIntersect,
    presets,
    minDate,
    suggestions,
    showSubTriggerFilters,
  };
}
